import { useState, useRef, useEffect } from "react";
import { ArrowRight, RefreshCcw, UtensilsCrossed, AlertTriangle, X, Info } from "lucide-react";
import MealPlanner from "@/components/MealPlanner";
import {
  calcBMR,
  calcTDEE,
  calcGoalCalories,
  calcMacros,
  ACTIVITY,
  SAFETY_FLOOR,
  toKg,
  toCm,
} from "@/lib/macros";

const ACTIVITY_KEYS = ["sedentary", "light", "moderate", "very", "extreme"];
const GOALS = [
  { key: "lose", label: "Lose body fat" },
  { key: "maintain", label: "Maintain weight" },
  { key: "build", label: "Build muscle" },
];
const LOSS_RATES = [
  { kg: 0.25, label: "Steady · 0.25 kg / week" },
  { kg: 0.5, label: "Standard · 0.5 kg / week" },
  { kg: 0.75, label: "Aggressive · 0.75 kg / week" },
  { kg: 1.0, label: "Hard push · 1.0 kg / week" },
];

const EMPTY_FORM = {
  sex: "male",
  age: "",
  units: "imperial", // metric | imperial
  heightCm: "",
  heightFt: "",
  heightIn: "",
  weightKg: "",
  weightSt: "",
  weightLb: "",
  targetWeightKg: "",
  targetWeightSt: "",
  targetWeightLb: "",
  activity: "moderate",
  goal: "lose",
  weeklyLossKg: 0.5,
};

export default function MacroCalculator() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const resultsRef = useRef(null);

  // Diabetes disclaimer gate
  const [isDiabetic, setIsDiabetic] = useState(false);
  const [diabetesAcknowledged, setDiabetesAcknowledged] = useState(false);
  const [showDiabetesModal, setShowDiabetesModal] = useState(false);
  const [modalUnderstands, setModalUnderstands] = useState(false);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (showDiabetesModal) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showDiabetesModal]);

  const handleDiabetesToggle = (checked) => {
    if (checked) {
      // open the mandatory read-and-acknowledge modal
      setModalUnderstands(false);
      setShowDiabetesModal(true);
    } else {
      setIsDiabetic(false);
      setDiabetesAcknowledged(false);
    }
  };

  const confirmDiabetesModal = () => {
    if (!modalUnderstands) return;
    setIsDiabetic(true);
    setDiabetesAcknowledged(true);
    setShowDiabetesModal(false);
  };

  const dismissDiabetesModal = () => {
    // closing without acknowledging reverts the declaration
    setShowDiabetesModal(false);
    setIsDiabetic(false);
    setDiabetesAcknowledged(false);
    setModalUnderstands(false);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resolveHeightCm = () => {
    if (form.units === "metric") return parseFloat(form.heightCm);
    const ft = parseFloat(form.heightFt) || 0;
    const inch = parseFloat(form.heightIn) || 0;
    return ft * 30.48 + inch * 2.54;
  };
  const resolveWeightKg = () => {
    if (form.units === "metric") return parseFloat(form.weightKg);
    const st = parseFloat(form.weightSt) || 0;
    const lb = parseFloat(form.weightLb) || 0;
    return toKg.stlb(st, lb);
  };

  const submit = (e) => {
    e.preventDefault();
    setError("");

    // Hard gate: if user declared diabetic but didn't acknowledge the disclaimer,
    // re-open the modal and block submission.
    if (isDiabetic && !diabetesAcknowledged) {
      setModalUnderstands(false);
      setShowDiabetesModal(true);
      return;
    }

    const age = parseInt(form.age, 10);
    const weightKg = resolveWeightKg();
    const heightCm = resolveHeightCm();

    if (!age || age < 15 || age > 90) {
      setError("Please enter an age between 15 and 90.");
      return;
    }
    if (!heightCm || heightCm < 120 || heightCm > 230) {
      setError("Please enter a valid height.");
      return;
    }
    if (!weightKg || weightKg < 30 || weightKg > 300) {
      setError("Please enter a valid weight.");
      return;
    }

    const bmr = calcBMR({ sex: form.sex, weightKg, heightCm, age });
    const tdee = calcTDEE(bmr, form.activity);
    let goalCalories = calcGoalCalories({
      tdee,
      goal: form.goal,
      weeklyLossKg: form.weeklyLossKg,
    });

    // Safety floor
    const floor = SAFETY_FLOOR[form.sex];
    let warning = "";
    if (goalCalories < floor) {
      warning = `Your target was below a safe minimum (${floor} kcal). It has been raised to ${floor} kcal. Consider a less aggressive weekly rate.`;
      goalCalories = floor;
    }

    const macros = calcMacros({ weightKg, goalCalories, goal: form.goal });
    const deficitOrSurplus = Math.round(goalCalories - tdee);

    setResult({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      goalCalories,
      ...macros,
      deficitOrSurplus,
      goal: form.goal,
      warning,
      weightKg: Math.round(weightKg * 10) / 10,
    });

    // scroll to results
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const reset = () => {
    setResult(null);
    setError("");
  };

  // Keep units toggle visible before results
  return (
    <div>
      {!result && (
        <form
          onSubmit={submit}
          className="bg-white border border-black/10 shadow-[0_30px_60px_rgba(0,0,0,0.06)] p-6 md:p-10"
          data-testid="macro-calculator-form"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="dta-overline text-navy">DTA Macro Calculator</p>
              <h2 className="section-heading text-3xl md:text-5xl mt-2">
                Know your numbers.
              </h2>
              <p className="text-sm text-[#272727]/70 mt-3 max-w-md">
                Private by design. Nothing is saved. Everything is calculated in your browser and cleared the moment you close this tab.
              </p>
            </div>

            {/* Unit toggle */}
            <div className="inline-flex border border-black/15">
              {["metric", "imperial"].map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => set("units", u)}
                  className={`px-4 py-2 dta-overline text-[0.65rem] transition-colors ${
                    form.units === u
                      ? "bg-[#031D47] text-white"
                      : "bg-transparent text-[#272727] hover:bg-black/5"
                  }`}
                  data-testid={`macro-units-${u}`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>

          {/* Form body — 2-column on desktop (landscape), stacked on mobile (portrait) */}
          <div className="md:grid md:grid-cols-2 md:gap-x-10">
            <div>
              {/* Sex */}
              <Field label="Sex">
                <div className="flex gap-2">
                  {["male", "female"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => set("sex", s)}
                      className={`flex-1 py-3 border transition-colors font-medium ${
                        form.sex === s
                          ? "border-[#031D47] bg-[#031D47] text-white"
                          : "border-black/20 hover:border-black"
                      }`}
                      data-testid={`macro-sex-${s}`}
                    >
                      {s === "male" ? "Male" : "Female"}
                    </button>
                  ))}
                </div>
              </Field>

              {/* Age */}
              <Field label="Age (years)">
                <input
                  type="number"
                  min="15"
                  max="90"
                  value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  className="w-full py-3 px-3 border border-black/20 bg-white"
                  required
                  data-testid="macro-age"
                />
              </Field>

              {/* Height */}
              <Field label="Height">
                {form.units === "metric" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={form.heightCm}
                      onChange={(e) => set("heightCm", e.target.value)}
                      className="w-full py-3 px-3 border border-black/20"
                      placeholder="e.g. 178"
                      required
                      data-testid="macro-height-cm"
                    />
                    <span className="dta-overline text-[0.65rem] text-[#272727]/70">cm</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="number"
                        value={form.heightFt}
                        onChange={(e) => set("heightFt", e.target.value)}
                        className="w-full py-3 px-3 border border-black/20"
                        placeholder="ft"
                        required
                        data-testid="macro-height-ft"
                      />
                      <span className="dta-overline text-[0.65rem] text-[#272727]/70">ft</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="number"
                        value={form.heightIn}
                        onChange={(e) => set("heightIn", e.target.value)}
                        className="w-full py-3 px-3 border border-black/20"
                        placeholder="in"
                        data-testid="macro-height-in"
                      />
                      <span className="dta-overline text-[0.65rem] text-[#272727]/70">in</span>
                    </div>
                  </div>
                )}
              </Field>

              {/* Weight */}
              <Field label="Current weight">
                {form.units === "metric" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={form.weightKg}
                      onChange={(e) => set("weightKg", e.target.value)}
                      className="w-full py-3 px-3 border border-black/20"
                      placeholder="e.g. 82"
                      required
                      data-testid="macro-weight-kg"
                    />
                    <span className="dta-overline text-[0.65rem] text-[#272727]/70">kg</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="number"
                        value={form.weightSt}
                        onChange={(e) => set("weightSt", e.target.value)}
                        className="w-full py-3 px-3 border border-black/20"
                        placeholder="st"
                        required
                        data-testid="macro-weight-st"
                      />
                      <span className="dta-overline text-[0.65rem] text-[#272727]/70">st</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="number"
                        value={form.weightLb}
                        onChange={(e) => set("weightLb", e.target.value)}
                        className="w-full py-3 px-3 border border-black/20"
                        placeholder="lb"
                        data-testid="macro-weight-lb"
                      />
                      <span className="dta-overline text-[0.65rem] text-[#272727]/70">lb</span>
                    </div>
                  </div>
                )}
              </Field>
            </div>

            {/* Right column: activity, goal, loss rate */}
            <div>
              {/* Activity level */}
              <Field label="Activity level">
                <div className="grid grid-cols-1 gap-2">
                  {ACTIVITY_KEYS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => set("activity", k)}
                      data-testid={`macro-activity-${k}`}
                      className={`text-left py-3 px-4 border transition-colors ${
                        form.activity === k
                          ? "border-[#031D47] bg-[#031D47]/5"
                          : "border-black/15 hover:border-black"
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-display uppercase font-bold text-base md:text-lg">{ACTIVITY[k].label}</span>
                        <span className="dta-overline text-[0.6rem] text-[#031D47]">
                          ×{ACTIVITY[k].mult}
                        </span>
                      </div>
                      <p className="text-xs text-[#272727]/70 mt-1">{ACTIVITY[k].note}</p>
                    </button>
                  ))}
                </div>
              </Field>

              {/* Goal */}
              <Field label="Goal">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {GOALS.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => set("goal", g.key)}
                      data-testid={`macro-goal-${g.key}`}
                      className={`py-3 px-4 border transition-colors font-medium ${
                        form.goal === g.key
                          ? "border-[#031D47] bg-[#031D47] text-white"
                          : "border-black/20 hover:border-black"
                      }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </Field>

              {/* Rate of weight loss (only visible if lose) */}
              {form.goal === "lose" && (
                <Field label="Rate of weight loss">
                  <div className="grid grid-cols-1 gap-2">
                    {LOSS_RATES.map((r) => (
                      <button
                        key={r.kg}
                        type="button"
                        onClick={() => set("weeklyLossKg", r.kg)}
                        data-testid={`macro-rate-${r.kg}`}
                        className={`py-3 px-4 border text-left transition-colors ${
                          form.weeklyLossKg === r.kg
                            ? "border-[#031D47] bg-[#031D47]/5"
                            : "border-black/15 hover:border-black"
                        }`}
                      >
                        <span className="font-display uppercase font-bold text-sm">
                          {r.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#272727]/70 mt-3">
                    Your target should be sustainable. Extremely low calorie targets may not provide adequate nutrition.
                  </p>
                </Field>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-3 border border-red-700/40 bg-red-50 text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              {error}
            </div>
          )}

          {/* Diabetes disclosure */}
          <div
            className="mb-6 border border-black/15 bg-[#F4F3EF] p-5"
            data-testid="macro-diabetes-block"
          >
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isDiabetic}
                onChange={(e) => handleDiabetesToggle(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#031D47] cursor-pointer shrink-0"
                data-testid="macro-diabetes-checkbox"
              />
              <div>
                <span className="font-display uppercase font-bold text-sm md:text-base text-[#0A0A0A]">
                  I have diabetes
                </span>
                <p className="text-xs md:text-sm text-[#272727]/75 mt-1 leading-relaxed">
                  Tick this box if you have diabetes. We'll show you an important note about how carbohydrate targets may affect your diabetes management before giving you your macros.
                </p>
                {diabetesAcknowledged && (
                  <p
                    className="dta-overline text-[0.6rem] text-[#031D47] mt-3 inline-flex items-center gap-1.5"
                    data-testid="macro-diabetes-acknowledged"
                  >
                    <Info className="w-3 h-3" strokeWidth={2} /> Diabetes advisory acknowledged
                  </p>
                )}
              </div>
            </label>
          </div>

          <button
            type="submit"
            className="dta-btn dta-btn-dark w-full md:w-auto"
            data-testid="macro-calculate"
          >
            Calculate My Macros <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </form>
      )}

      {/* Diabetes disclaimer modal */}
      {showDiabetesModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="diabetes-modal-title"
          data-testid="macro-diabetes-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) dismissDiabetesModal();
          }}
        >
          <div className="bg-[#F4F3EF] border border-black/15 max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-[0_40px_90px_rgba(0,0,0,0.5)]">
            <div className="flex items-start justify-between gap-4 p-6 md:p-8 pb-0">
              <div>
                <p className="dta-overline text-navy">Important health notice</p>
                <h3
                  id="diabetes-modal-title"
                  className="font-display uppercase font-bold text-2xl md:text-3xl mt-3 leading-tight text-[#0A0A0A]"
                >
                  Diabetes &amp; Macro Targets
                </h3>
              </div>
              <button
                type="button"
                onClick={dismissDiabetesModal}
                className="shrink-0 p-1 text-[#272727] hover:text-[#031D47] transition-colors"
                aria-label="Close"
                data-testid="macro-diabetes-modal-close"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <div className="p-6 md:p-8 pt-5 space-y-4 text-sm md:text-base text-[#272727] leading-relaxed">
              <p>
                Your calorie and macro results are general estimates. Diabetes can affect how carbohydrate intake should be managed, particularly if you use insulin or glucose-lowering medication.
              </p>
              <p>
                Do not make significant changes to carbohydrate intake or medication based solely on this calculator. Speak with your diabetes healthcare team or dietitian for personalised advice.
              </p>
            </div>
            <div className="mx-6 md:mx-8 border-t border-black/15 pt-5 pb-6 md:pb-8">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={modalUnderstands}
                  onChange={(e) => setModalUnderstands(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#031D47] cursor-pointer shrink-0"
                  data-testid="macro-diabetes-understand-checkbox"
                />
                <span className="text-sm md:text-base text-[#272727] leading-relaxed">
                  I understand this calculator does not replace advice from my diabetes healthcare team or dietitian.
                </span>
              </label>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={dismissDiabetesModal}
                  className="dta-btn dta-btn-outline-dark"
                  data-testid="macro-diabetes-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDiabetesModal}
                  disabled={!modalUnderstands}
                  className={`dta-btn dta-btn-dark ${
                    !modalUnderstands ? "opacity-40 cursor-not-allowed" : ""
                  }`}
                  data-testid="macro-diabetes-continue"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div ref={resultsRef} className="scroll-mt-24">
          <Results result={result} onReset={reset} />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-7">
      <span className="dta-overline text-[0.65rem] text-[#272727] block mb-3">{label}</span>
      {children}
    </label>
  );
}

function Results({ result, onReset }) {
  const { bmr, tdee, goalCalories, proteinG, carbsG, fatG, deficitOrSurplus, goal, warning } = result;

  const proteinKcal = proteinG * 4;
  const carbsKcal = carbsG * 4;
  const fatKcal = fatG * 9;
  const totalKcal = proteinKcal + carbsKcal + fatKcal;
  const pct = (n) => (totalKcal ? Math.round((n / totalKcal) * 100) : 0);

  const goalLabel =
    goal === "lose" ? "Fat Loss" : goal === "build" ? "Muscle Build" : "Maintenance";
  const deltaLabel =
    goal === "lose" ? "Daily deficit" : goal === "build" ? "Daily surplus" : "At maintenance";

  return (
    <div className="bg-white border border-black/10 shadow-[0_30px_60px_rgba(0,0,0,0.06)] p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <p className="dta-overline text-navy">Your daily target</p>
          <h2 className="section-heading text-3xl md:text-5xl mt-2">
            {goalLabel.toLowerCase()}.
          </h2>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="dta-overline text-[0.65rem] inline-flex items-center gap-2 text-[#031D47] hover:opacity-70"
          data-testid="macro-reset"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} /> Adjust inputs
        </button>
      </div>

      {warning && (
        <div className="mb-8 p-4 border border-amber-700/40 bg-amber-50 text-sm text-amber-900 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
          <span>{warning}</span>
        </div>
      )}

      {/* Big number */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 border-y border-black/10 py-8">
        <div>
          <p className="dta-overline text-[0.65rem] text-[#272727]">Calories</p>
          <p className="font-display font-black text-6xl md:text-8xl leading-none mt-2">
            {goalCalories.toLocaleString()}
          </p>
          <p className="dta-overline text-[0.6rem] text-[#272727]/60 mt-2">kcal / day</p>
        </div>
        <div className="grid grid-cols-3 gap-3 md:gap-5 self-center">
          <Stat label="Maintenance" value={tdee.toLocaleString()} unit="kcal" />
          <Stat label="BMR" value={bmr.toLocaleString()} unit="kcal" />
          <Stat
            label={deltaLabel}
            value={
              deficitOrSurplus === 0
                ? "0"
                : `${deficitOrSurplus < 0 ? "−" : "+"}${Math.abs(deficitOrSurplus).toLocaleString()}`
            }
            unit="kcal"
            tone={
              deficitOrSurplus === 0
                ? ""
                : deficitOrSurplus < 0
                ? "text-[#031D47]"
                : "text-[#4E5B43]"
            }
          />
        </div>
      </div>

      {/* Macros */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <MacroCard colour="#031D47" label="Protein" grams={proteinG} kcal={proteinKcal} pct={pct(proteinKcal)} />
        <MacroCard colour="#B7AA8D" label="Carbohydrates" grams={carbsG} kcal={carbsKcal} pct={pct(carbsKcal)} />
        <MacroCard colour="#4E5B43" label="Fat" grams={fatG} kcal={fatKcal} pct={pct(fatKcal)} />
      </div>

      {/* Meal planner — optional meal planning layer */}
      <div className="mt-10">
        <MealPlanner
          dailyTarget={{
            calories: goalCalories,
            protein: proteinG,
            carbs: carbsG,
            fat: fatG,
          }}
        />
      </div>

      {/* Disclaimer */}
      <p className="mt-10 text-xs text-[#272727]/60 leading-relaxed">
        DTA provides estimated calorie and macronutrient targets for general nutritional planning purposes. Individual nutritional requirements can vary. If you have a medical condition, are pregnant, have a history of eating disorders, or require specialist dietary advice, consult an appropriately qualified healthcare professional or dietitian.
      </p>
    </div>
  );
}

function Stat({ label, value, unit = "", tone = "" }) {
  return (
    <div className="min-w-0">
      <p className="dta-overline text-[0.55rem] text-[#272727]/70 leading-tight break-words">
        {label}
      </p>
      <p className={`font-display font-bold text-xl md:text-2xl lg:text-3xl leading-none mt-2 ${tone} truncate`}>
        {value}
      </p>
      <p className="dta-overline text-[0.55rem] text-[#272727]/60 mt-1">{unit}</p>
    </div>
  );
}

function MacroCard({ colour, label, grams, kcal, pct }) {
  return (
    <div className="border border-black/10 p-5">
      <div className="flex items-baseline justify-between">
        <p className="dta-overline text-[0.65rem] text-[#272727]">{label}</p>
        <p className="dta-overline text-[0.6rem]" style={{ color: colour }}>
          {pct}%
        </p>
      </div>
      <p className="font-display font-black text-4xl md:text-5xl leading-none mt-3">
        {grams}
        <span className="font-display text-xl ml-1 opacity-60">g</span>
      </p>
      <p className="dta-overline text-[0.55rem] text-[#272727]/60 mt-2">
        {kcal.toLocaleString()} kcal
      </p>
      {/* visual bar */}
      <div className="mt-4 h-1.5 bg-black/10 w-full overflow-hidden">
        <div
          className="h-full"
          style={{ width: `${Math.min(100, Math.max(2, pct))}%`, backgroundColor: colour }}
        />
      </div>
    </div>
  );
}
